import { BaseApi, HttpMethodEnum } from '../BaseApi/BaseApi';
import { S3UploadPolicy } from '../Documents/DocumentApi.ts';
import {
  IEICFConsent,
  IRefuseEICFConsentRequest,
  ISubmitEICFSignatureRequest,
} from '../../entities/EICFConsents/EICFConsent.ts';
import { getFilenameFromContentDisposition } from '../../helpers/getFilenameFromContentDisposition.ts';

export class EICFConsentsApi extends BaseApi {
  private static baseApiUrl: string = `${BaseApi.apiUrl}/EICFConsents`;

  public static apiUrls = {
    active: `${EICFConsentsApi.baseApiUrl}/active`,
    mySigned: `${EICFConsentsApi.baseApiUrl}/my-signed`,
    pendingPhysician: `${EICFConsentsApi.baseApiUrl}/pending-physician`,
    signedPhysician: `${EICFConsentsApi.baseApiUrl}/signed-physician`,
    patientSignature: `${EICFConsentsApi.baseApiUrl}/patient-signature`,
    physicianSignature: `${EICFConsentsApi.baseApiUrl}/physician-signature`,
    refuse: `${EICFConsentsApi.baseApiUrl}/refuse`,
    canSign: `${EICFConsentsApi.baseApiUrl}/can-sign`,
    consents: EICFConsentsApi.baseApiUrl,
  };

  public static async getActive(): Promise<IEICFConsent | null> {
    return this.sendApiRequest<IEICFConsent | null>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.active,
      data: undefined,
    });
  }

  public static async getMySigned(): Promise<IEICFConsent | null> {
    return this.sendApiRequest<IEICFConsent | null>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.mySigned,
      data: undefined,
    });
  }

  public static async getPendingForPhysician(
    facilityId?: number,
  ): Promise<IEICFConsent[]> {
    const query = facilityId ? `?facilityId=${facilityId}` : '';
    return this.sendApiRequest<IEICFConsent[]>({
      method: HttpMethodEnum.GET,
      path: `${this.apiUrls.pendingPhysician}${query}`,
      data: undefined,
    });
  }

  public static async getSignedForPhysician(
    facilityId?: number,
  ): Promise<IEICFConsent[]> {
    const query = facilityId ? `?facilityId=${facilityId}` : '';
    return this.sendApiRequest<IEICFConsent[]>({
      method: HttpMethodEnum.GET,
      path: `${this.apiUrls.signedPhysician}${query}`,
      data: undefined,
    });
  }

  public static async getSignedPdfUploadUrl(
    consentId: number,
    filename: string,
  ): Promise<S3UploadPolicy> {
    return this.sendApiRequest<S3UploadPolicy>({
      method: HttpMethodEnum.GET,
      path: `${this.apiUrls.consents}/${consentId}/signed-pdf-upload-url?filename=${filename}`,
    });
  }

  public static async submitPatientSignature(
    data: ISubmitEICFSignatureRequest,
  ): Promise<IEICFConsent> {
    return this.sendApiRequest<IEICFConsent>({
      method: HttpMethodEnum.POST,
      path: this.apiUrls.patientSignature,
      data,
    });
  }

  public static async getPhysicianSignedPdfUploadUrl(
    consentId: number,
    filename: string,
  ): Promise<S3UploadPolicy> {
    return this.sendApiRequest<S3UploadPolicy>({
      method: HttpMethodEnum.GET,
      path: `${this.apiUrls.consents}/${consentId}/physician-signed-pdf-upload-url?filename=${filename}`,
    });
  }

  public static async submitPhysicianSignature(
    data: ISubmitEICFSignatureRequest,
  ): Promise<IEICFConsent> {
    return this.sendApiRequest<IEICFConsent>({
      method: HttpMethodEnum.POST,
      path: this.apiUrls.physicianSignature,
      data,
    });
  }

  public static async refuse(
    data: IRefuseEICFConsentRequest,
  ): Promise<IEICFConsent> {
    return this.sendApiRequest<IEICFConsent>({
      method: HttpMethodEnum.POST,
      path: this.apiUrls.refuse,
      data,
    });
  }

  public static async canSign(): Promise<boolean> {
    return this.sendApiRequest<boolean>({
      method: HttpMethodEnum.GET,
      path: this.apiUrls.canSign,
      data: undefined,
    });
  }

  public static async downloadPdf(
    id: number,
    language: string,
  ): Promise<{ blob: Blob; filename: string | null }> {
    const { ApiManager } = await import('../ApiManager/ApiManager.tsx');
    const response = await ApiManager.get<Blob>(
      `${this.apiUrls.consents}/${id}/pdf?language=${language}`,
      { responseType: 'blob' },
    );
    const filename = getFilenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
    );
    return { blob: response.data, filename };
  }

  public static async downloadMySignedPdf(
    language: string,
  ): Promise<{ blob: Blob; filename: string | null }> {
    const { ApiManager } = await import('../ApiManager/ApiManager.tsx');
    const response = await ApiManager.get<Blob>(
      `${this.apiUrls.mySigned}/pdf?language=${language}`,
      { responseType: 'blob' },
    );
    const filename = getFilenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
    );
    return { blob: response.data, filename };
  }

  public static async sendToAssistant(
    id: number,
    language: string,
  ): Promise<{ assistantName: string; assistantEmail: string }> {
    return this.sendApiRequest<{
      assistantName: string;
      assistantEmail: string;
    }>({
      method: HttpMethodEnum.POST,
      path: `${this.apiUrls.consents}/${id}/SendToAssistant?language=${language}`,
    });
  }

  public static downloadPdfFile(pdfBlob: Blob, filename?: string): void {
    const url = window.URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download =
      filename || `Consent_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
